/*
 * ChasingCards — Warenkorb-Empfehlungen (<cart-recommendations>).
 * Zwei Modi (per data-mode gesetzt in cart-drawer.liquid):
 *  - "collection": Inhalt ist server-seitig schon drin (aus der gewählten
 *    Collection). KEIN Fetch. Bei order=random enthüllt das JS genau `count`
 *    Karten gemischt — einmal pro Drawer-Öffnung stabil (sessionStorage),
 *    beim Schließen zurückgesetzt. Kein Aufblitzen (Pool ist per CSS versteckt).
 *  - "related" (Fallback, keine Collection gewählt): leeres Element, JS fetcht
 *    async die Recommendations-API erst beim Öffnen (kein Seitenload-Cost).
 * Gemeinsam: Einklappen (+ localStorage-Persistenz), navy „+"-Add (löst den
 * Drawer-Re-Render aus), Auto-Collapse nach +Add.
 */
class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
    this._revealed = false;
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
    const drawer = this.closest('cart-drawer');
    this._drawer = drawer;

    if (this.dataset.mode === 'collection') {
      // Inhalt ist bereits da → Collapse sofort anwenden (Drawer ist beim
      // Seitenload off-canvas → kein Flash). Random erst beim Öffnen enthüllen.
      this._applyCollapsedNow();
      if (drawer && drawer.classList.contains('active')) this._revealRandom();
      if (drawer) {
        this._observer = new MutationObserver(() => {
          if (drawer.classList.contains('active')) {
            this._revealRandom();
          } else {
            this._clearRand(); // Schließen → nächste Öffnung mischt neu
          }
        });
        this._observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
      return;
    }

    // related-Fallback: erst laden, wenn der Drawer offen ist.
    if (drawer && drawer.classList.contains('active')) {
      this.load();
    } else if (drawer) {
      this._observer = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          this._observer.disconnect();
          this._observer = null;
          this.load();
        }
      });
      this._observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  /* ---------- related-Fallback: async fetch ---------- */
  load() {
    if (this.loaded) return;
    this.loaded = true;

    const seed = this.dataset.productId;
    const url = this.dataset.url;
    if (!seed || !url) {
      this.hidden = true;
      return;
    }

    const params = new URLSearchParams({
      'section_id': 'cart-recommendations',
      'product_id': seed,
      'limit': this.dataset.limit || '3',
      'intent': this.dataset.intent || 'related',
    });

    fetch(`${url}?${params.toString()}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('.cc-cart-recs');
        if (content) {
          if (this._isCollapsed()) {
            content.classList.add('is-collapsed', 'cc-cart-recs--no-anim');
            const toggle = content.querySelector('.cc-cart-recs__toggle');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
          }
          this.innerHTML = content.outerHTML;
          this.hidden = false;
          const root = this.querySelector('.cc-cart-recs');
          if (root && root.classList.contains('cc-cart-recs--no-anim')) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => root.classList.remove('cc-cart-recs--no-anim'));
            });
          }
        } else {
          this.innerHTML = '';
          this.hidden = true;
        }
      })
      .catch(() => {
        this.innerHTML = '';
        this.hidden = true;
      });
  }

  /* ---------- Collection-Modus: Collapse-State auf vorhandenen Inhalt ---------- */
  _applyCollapsedNow() {
    const root = this.querySelector('.cc-cart-recs');
    if (!root) return;
    if (this._isCollapsed()) {
      root.classList.add('is-collapsed', 'cc-cart-recs--no-anim');
      const toggle = root.querySelector('.cc-cart-recs__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => root.classList.remove('cc-cart-recs--no-anim'));
      });
    }
  }

  /* ---------- Collection + random: genau `count` gemischt enthüllen ---------- */
  _revealRandom() {
    if (this._revealed) return;
    const list = this.querySelector('.cc-cart-recs__list--pool');
    if (!list) {
      this._revealed = true; // fixed → nichts zu tun
      return;
    }
    this._revealed = true;

    const count = parseInt(list.dataset.count || '3', 10);
    const items = Array.from(list.querySelectorAll('.cc-cart-recs__item'));
    // Server hat bereits gegen den Warenkorb dedupliziert → das ist der
    // aktuell zulässige Pool.
    const poolIds = items.map((li) => li.dataset.productId);

    // Gespeicherte Reihenfolge (pro Öffnung stabil) oder neu mischen.
    let order = this._getRand();
    if (!order) {
      order = this._shuffle(poolIds.slice());
      this._setRand(order);
    }

    // Reconcile: aus der gespeicherten Reihenfolge die IDs nehmen, die JETZT
    // noch im Pool sind (grade in den Cart gelegte fallen raus → nächste rückt
    // nach, keine Lücke), dann evtl. neue Pool-IDs ohne Eintrag ans Ende.
    const chosen = [];
    order.forEach((id) => {
      if (chosen.length < count && poolIds.indexOf(id) !== -1) chosen.push(id);
    });
    poolIds.forEach((id) => {
      if (chosen.length < count && chosen.indexOf(id) === -1) chosen.push(id);
    });

    items.forEach((li) => {
      const idx = chosen.indexOf(li.dataset.productId);
      if (idx !== -1) {
        li.style.order = String(idx);
        li.classList.add('cc-cart-recs__item--show');
      } else {
        li.classList.remove('cc-cart-recs__item--show');
      }
    });
  }

  _shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  _getRand() {
    try {
      const raw = sessionStorage.getItem('cc-cart-recs-rand');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  _setRand(order) {
    try {
      sessionStorage.setItem('cc-cart-recs-rand', JSON.stringify(order));
    } catch (e) {
      /* sessionStorage nicht verfügbar → Reihenfolge nur für diesen Render */
    }
  }

  _clearRand() {
    try {
      sessionStorage.removeItem('cc-cart-recs-rand');
    } catch (e) {
      /* egal */
    }
  }

  /* ---------- Einklappen (localStorage-Persistenz) ---------- */
  _isCollapsed() {
    try {
      return localStorage.getItem('cc-cart-recs-collapsed') === '1';
    } catch (e) {
      return false;
    }
  }

  _setCollapsed(collapsed) {
    try {
      localStorage.setItem('cc-cart-recs-collapsed', collapsed ? '1' : '0');
    } catch (e) {
      /* localStorage nicht verfügbar → nur UI-Zustand, kein Persist */
    }
  }

  _onClick(event) {
    const toggle = event.target.closest('.cc-cart-recs__toggle');
    if (toggle) {
      event.preventDefault();
      this._toggleCollapse(toggle);
      return;
    }
    const button = event.target.closest('.cc-cart-recs__add');
    if (!button) return;
    event.preventDefault();
    this._addToCart(button);
  }

  _toggleCollapse(toggle) {
    const root = this.querySelector('.cc-cart-recs');
    if (!root) return;
    const collapsed = !root.classList.contains('is-collapsed');
    root.classList.toggle('is-collapsed', collapsed);
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    this._setCollapsed(collapsed);
  }

  _addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    const drawer = this.closest('cart-drawer');
    const sections =
      drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((s) => s.id).join(',')
        : 'cart-drawer,cart-icon-bubble';

    const routesRoot = (window.routes && window.routes.cart_add_url) || `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add`;

    fetch(routesRoot, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
          return;
        }
        // Nach dem Hinzufügen: Sektion automatisch zuklappen (persistiert) —
        // bleibt zu, bis der Nutzer sie per Toggle wieder öffnet.
        this._setCollapsed(true);
        if (drawer && typeof drawer.renderContents === 'function') {
          // Re-rendert #CartDrawer inkl. Balken, Summe UND frisches
          // <cart-recommendations>. Collection-Modus: server dedupliziert das
          // gerade hinzugefügte Produkt weg; die gespeicherte Random-Reihenfolge
          // bleibt (nur die zulässigen IDs rücken nach).
          drawer.renderContents(state);
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: variantId,
          });
        }
      })
      .catch(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('cart-recommendations', CartRecommendations);
